import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Stop,
  Replay,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard, CardTitle, SecondaryButton } from '../styles/sharedStyles';

const MaintenanceContainers = ({ apiBaseUrl, token }) => {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // container id

  const fetchContainers = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${apiBaseUrl}/api/containers`, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      setContainers(response.data.containers || []);
      if (response.data.error) setError(response.data.error);
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expired' : err.message || 'Failed to load containers');
      if (err.response?.status === 401) return;
      setContainers([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchContainers();
  }, [fetchContainers]);

  const runAction = async (containerId, action) => {
    setActionLoading(containerId);
    try {
      await axios.post(
        `${apiBaseUrl}/api/containers/${containerId}/${action}`,
        {},
        {
          timeout: 30000,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchContainers();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const isRunning = (c) => (c.status || c.state || '').toLowerCase() === 'running';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => { setLoading(true); fetchContainers(); }}
          disabled={loading}
          sx={{ color: '#00d4ff' }}
          title="Refresh"
        >
          <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading && containers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </Box>
      ) : containers.length === 0 ? (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
          No containers found. Mount /var/run/docker.sock in the backend to manage Docker containers.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {containers.map((c) => (
            <Grid item xs={12} sm={6} lg={4} key={c.id}>
              <GlassmorphicCard sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                  <CardTitle sx={{ fontSize: '1rem', wordBreak: 'break-all' }}>{c.name || c.short_id}</CardTitle>
                  <Chip
                    size="small"
                    label={c.status || c.state || 'unknown'}
                    sx={{
                      bgcolor: isRunning(c) ? 'rgba(0,255,136,0.15)' : 'rgba(255,107,107,0.15)',
                      color: isRunning(c) ? '#69db7c' : '#ff6b6b',
                      border: `1px solid ${isRunning(c) ? 'rgba(0,255,136,0.4)' : 'rgba(255,107,107,0.4)'}`,
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                  {c.image}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', mb: 1.5 }}>
                  {c.short_id}
                </Typography>

                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!isRunning(c) && (
                    <Tooltip title="Start">
                      <span>
                        <SecondaryButton
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => runAction(c.id, 'start')}
                          disabled={actionLoading === c.id}
                        >
                          {actionLoading === c.id ? '…' : 'Start'}
                        </SecondaryButton>
                      </span>
                    </Tooltip>
                  )}
                  {isRunning(c) && (
                    <>
                      <Tooltip title="Stop">
                        <span>
                          <SecondaryButton
                            size="small"
                            startIcon={<Stop />}
                            onClick={() => runAction(c.id, 'stop')}
                            disabled={actionLoading === c.id}
                            sx={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,0.5)' }}
                          >
                            {actionLoading === c.id ? '…' : 'Stop'}
                          </SecondaryButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Restart">
                        <span>
                          <SecondaryButton
                            size="small"
                            startIcon={<Replay />}
                            onClick={() => runAction(c.id, 'restart')}
                            disabled={actionLoading === c.id}
                            sx={{ color: '#ffa726', borderColor: 'rgba(255,167,38,0.5)' }}
                          >
                            {actionLoading === c.id ? '…' : 'Restart'}
                          </SecondaryButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </GlassmorphicCard>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MaintenanceContainers;
